import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import AlternativeSelect, { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import Business from '@material-ui/icons/Business';
import Add from '@material-ui/icons/Add';
import Done from '@material-ui/icons/Done';
import styles from 'assets/jss/material-dashboard-pro-react/components/departmentsPickerStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import classNames from 'classnames';
import { BodyS, Caption } from 'components/Typography/Typography';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IGroupedDepartment extends IAlternativeSelectItem {
	departments: SingleDepartment[];
	id: string;
}

interface IDepartmentsPickerProps {
	accounts: types.users.ISingleAccount[];
	departments: SingleDepartment[];
	selectedDepartments: string[];
	setSelectedDepartments: (value: string[]) => void;
}

const DepartmentsPicker = ({
	accounts,
	departments,
	setSelectedDepartments,
	selectedDepartments,
}: IDepartmentsPickerProps) => {
	const [selectedAccount, setSelectedAccount] = useState<IGroupedDepartment | null>(null);
	const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
	const [selectedGroupedDepartments, setSelectedGroupedDepartments] = useState<IGroupedDepartment[]>([]);
	const classes = useStyles();

	const groupedDepartments: IGroupedDepartment[] = useMemo(() => {
		return accounts.map((account) => {
			const filteredDepartments = departments.filter((department) => String(department.accountId) === account.id);
			return {
				...account,
				departments: filteredDepartments,
			};
		});
	}, [accounts, departments]);

	useEffect(() => {
		if (selectedAccount) {
			setSelectedGroupedDepartments(groupedDepartments.filter((account) => account.id === selectedAccount?.id));
		} else {
			setSelectedGroupedDepartments(groupedDepartments);
		}
	}, [groupedDepartments, selectedAccount]);

	const selectAccount = (account: IGroupedDepartment) => {
		const index = selectedAccounts.indexOf(account.id);
		if (index >= 0) {
			setSelectedAccounts((_accounts) => {
				const _selectedAccounts = [..._accounts];
				_selectedAccounts.splice(index, 1);
				return _selectedAccounts;
			});
			removeAccountsDepartments(account.departments);
		} else {
			setSelectedAccounts([...selectedAccounts, account.id]);
			addAccountsDepartments(account.departments);
		}
	};

	const uncheckAccountIfNecessary = (accountId: string) => {
		const index = selectedAccounts.indexOf(accountId);
		if (index >= 0) {
			const _selectedAccounts = [...selectedAccounts];
			_selectedAccounts.splice(index, 1);
			setSelectedAccounts(_selectedAccounts);
		}
	};

	const checkAccountIfNecessary = (accountId: string, _selectedDepartments: string[]) => {
		const availableDepartments = groupedDepartments.find(
			(account: IGroupedDepartment) => account.id === String(accountId)
		)?.departments;
		const diff = _.differenceWith(
			availableDepartments || [],
			_selectedDepartments,
			(availableDepartment, deptId) => availableDepartment.deptId === deptId
		);
		if (diff.length === 0) {
			setSelectedAccounts([...selectedAccounts, accountId]);
		}
	};

	const addAccountsDepartments = (departments: SingleDepartment[]) => {
		const departmentsToAdd: string[] = [];
		departments.forEach((department) => {
			if (selectedDepartments.indexOf(department.deptId) < 0) {
				departmentsToAdd.push(department.deptId);
			}
		});
		setSelectedDepartments([...selectedDepartments, ...departmentsToAdd]);
	};

	const removeAccountsDepartments = (departments: SingleDepartment[]) => {
		const _selectedDepartments = [...selectedDepartments];
		departments.forEach((department) => {
			const index = _selectedDepartments.indexOf(department.deptId);
			if (index >= 0) {
				_selectedDepartments.splice(index, 1);
			}
		});
		setSelectedDepartments(_selectedDepartments);
	};

	const selectDepartment = (department: SingleDepartment) => {
		const index = selectedDepartments.indexOf(department.deptId);
		if (index >= 0) {
			const _selectedDepartments = [...selectedDepartments];
			_selectedDepartments.splice(index, 1);
			setSelectedDepartments(_selectedDepartments);
			uncheckAccountIfNecessary(department.accountId);
		} else {
			const _selectedDepartments = [...selectedDepartments, department.deptId];
			setSelectedDepartments(_selectedDepartments);
			checkAccountIfNecessary(department.accountId, _selectedDepartments);
		}
	};

	return (
		<div>
			<AlternativeSelect
				className={classes.alternativeSelect}
				value={
					<div className={classes.selectedItem}>
						<BodyS>{selectedAccount ? selectedAccount.name : translate('all.organizations')}</BodyS>
					</div>
				}
				onClick={(item) => {
					if (selectedAccount && selectedAccount.id === item.id) {
						setSelectedAccount(null);
					} else {
						setSelectedAccount(item);
					}
				}}
				items={accounts}
				selectedItems={selectedAccount ? [selectedAccount.id] : []}
			/>
			<div>
				{selectedGroupedDepartments.map((account) => (
					<div key={account.id}>
						<div
							className={classNames({
								[classes.accountWrapper]: true,
								[classes.disabledAccountWrapper]: account.departments.length <= 0,
							})}
						>
							<CustomCheckbox
								disableLabel
								checked={selectedAccounts.indexOf(account.id) >= 0}
								onClick={() => selectAccount(account)}
								disabled={account.departments.length <= 0}
							/>
							<div
								className={classNames({
									[classes.selectedItem]: true,
									[classes.disabledItem]: account.departments.length <= 0,
								})}
							>
								<Business />
								<BodyS color={account.departments.length <= 0 ? 'disabled-light' : 'primary-light'}>
									{account.name}
								</BodyS>
								{account.departments.length <= 0 && (
									<BodyS color="disabled-light">
										{translate('create.departments.to.add.permissions')}
									</BodyS>
								)}
							</div>
						</div>
						<div className={classes.departmentsWrapper}>
							{account.departments.map((department) => (
								<div
									key={department.deptId}
									style={{
										backgroundColor: department.uiColor,
									}}
									className={classNames({
										[classes.notSelected]: selectedDepartments.indexOf(department.deptId) < 0,
									})}
									onClick={() => selectDepartment(department)}
								>
									<Caption color="primary-dark">{department.name}</Caption>
									{selectedDepartments.indexOf(department.deptId) >= 0 ? <Done /> : <Add />}
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default DepartmentsPicker;
